import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  FaBook,
  FaGraduationCap,
  FaChalkboardTeacher,
  FaLaptop,
  FaMicroscope,
  FaGlobe,
} from "react-icons/fa";
import "./Academics.css";
import { ENQUIRY_URL, PU_ENQUIRY_URL } from "../config";
import AdmissionDropdown from "../components/AdmissionDropdown";

const Academics = () => {
  const curricula = [
    {
      id: "state-syllabus",
      icon: <FaBook />,
      name: "State Syllabus",
      board: "Karnataka State Board",
      description:
        "Following the Karnataka State Board curriculum, we provide quality education rooted in local context while maintaining high academic standards.",
      features: [
        "Kannada & English medium options",
        "Affordable fee structure",
        "Focus on state university admissions",
        "Regional language emphasis",
        "Traditional teaching methods combined with modern approach",
      ],
      grades: "Pre-Primary to Class X",
      image:
        "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=800",
    },
    {
      id: "cbse",
      icon: <FaGraduationCap />,
      name: "CBSE",
      board: "Central Board of Secondary Education",
      description:
        "Nationally recognized curriculum emphasizing conceptual understanding, analytical thinking, and practical application. Perfect preparation for competitive examinations.",
      features: [
        "NCERT curriculum aligned",
        "Competitive exam focused (JEE, NEET)",
        "Continuous & Comprehensive Evaluation (CCE)",
        "National recognition",
        "Digital learning integration",
      ],
      grades: "Pre-Primary to Class XII (Science, Commerce, Humanities)",
      image:
        "https://images.unsplash.com/photo-1509062522246-3755977927d7?w=800",
    },
    {
      id: "icse",
      icon: <FaMicroscope />,
      name: "ICSE",
      board: "Indian Certificate of Secondary Education",
      description:
        "Comprehensive curriculum with detailed subject knowledge and emphasis on English language proficiency. Ideal for students seeking in-depth understanding.",
      features: [
        "Detailed subject coverage",
        "Strong English language focus",
        "Practical & application-based learning",
        "Arts & humanities integration",
        "Recognized across India and abroad",
      ],
      grades: "Pre-Primary to Class X",
      image:
        "https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=800",
    },
    {
      id: "cambridge",
      icon: <FaGlobe />,
      name: "Cambridge International",
      board: "Cambridge Assessment International Education",
      description:
        "Internationally recognized British curriculum preparing students for global opportunities with IGCSE and A-Level qualifications.",
      features: [
        "International curriculum",
        "Global university recognition",
        "IGCSE & A-Level programs",
        "Critical thinking & creativity focus",
        "University pathway programs",
      ],
      grades: "Primary to A-Levels",
      image:
        "https://images.unsplash.com/photo-1427504494785-3a9ca7044f45?w=800",
    },
    {
      id: "pu-college",
      icon: <FaGraduationCap />,
      name: "PU College",
      board: "Karnataka Pre-University Board",
      description:
        "Sri Vani Vidya Kendra PU College offers a structured integrated program preparing students for both PU board exams and competitive entrance examinations.",
      features: [
        "Available at Sri Vani Since 2010",
        "Science stream: PCMB & PCMC combinations",
        "Commerce stream: SEBA, CEBA & BASBm",
        "Integrated CET, NEET, JEE & CA Foundation tracks",
        "Transport facility available",
      ],
      grades: "Classes XI & XII",
      image: "/images/pu/pu-campus.jpg",
    },
    {
      id: "degree-college",
      icon: <FaChalkboardTeacher />,
      name: "Degree College",
      board: "Bangalore University",
      description:
        "Sri Vani Vidya Kendra Degree College offers affordable, world-class undergraduate education across Commerce, Science, and Arts streams affiliated to Bangalore University.",
      features: [
        "Available at Sri Vani Since 2018",
        "B.Com, BBA, BCA programs",
        "B.Sc (PCM & PMCs) streams",
        "BA in Dance, Drama & Music",
        "Industrial visits & personality development",
      ],
      grades: "Undergraduate (3-Year Degree Programs)",
      image: "/images/degree/degree-hero.png",
    },
  ];

  const puStreams = [
    {
      name: "Science Stream (PCMB/PCM)",
      subjects: "Physics, Chemistry, Mathematics, Biology/Computer Science",
      career: "Engineering, Medicine, Research, IT",
      icon: "🔬",
    },
    {
      name: "Commerce Stream",
      subjects: "Accountancy, Business Studies, Economics, Mathematics",
      career: "CA, CS, Business Management, Banking",
      icon: "💼",
    },
    {
      name: "Arts/Humanities Stream",
      subjects: "History, Economics, Political Science, Psychology",
      career: "Law, Civil Services, Media, Social Work",
      icon: "📚",
    },
  ];

  const teachingMethods = [
    // {
    //   title: "Smart Classrooms",
    //   description:
    //     "Interactive digital boards, audio-visual aids, and multimedia content for engaging learning experiences",
    //   icon: <FaLaptop />,
    // },
    {
      title: "Experiential Learning",
      description:
        "Hands-on activities, field trips, and practical experiments to reinforce theoretical concepts",
      icon: <FaMicroscope />,
    },
    {
      title: "Personalized Attention",
      description:
        "Small class sizes, individual mentoring, and customized learning plans for each student",
      icon: <FaChalkboardTeacher />,
    },
    {
      title: "Project-Based Learning",
      description:
        "Real-world projects, research assignments, and collaborative group work",
      icon: <FaBook />,
    },
  ];

  return (
    <div className="academics-page">
      {/* Page Header */}
      <section className="page-header">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1>Academics</h1>
            <p>
              Multiple curricula pathways to suit every student's aspirations
            </p>
          </motion.div>
        </div>
      </section>

      {/* Introduction */}
      <section className="intro-section section">
        <div className="container">
          <motion.div
            className="intro-content"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="section-title">Choose Your Educational Path</h2>
            <p>
              At Sri Vani Education, we offer multiple internationally
              recognized curricula to match your child's learning style and
              future aspirations. Whether you prefer the national boards or
              international programs, we provide world-class education with
              experienced faculty and state-of-the-art facilities.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Curriculum Sections */}
      {curricula.map((curriculum, index) => (
        <section
          key={curriculum.id}
          id={curriculum.id}
          className={`curriculum-section section ${index % 2 === 0 ? "bg-white" : "bg-light"}`}
        >
          <div className="container">
            <div
              className={`curriculum-content ${index % 2 === 0 ? "image-right" : "image-left"}`}
            >
              <motion.div
                className="curriculum-text"
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <div className="curriculum-icon-large">{curriculum.icon}</div>
                <h2 className="curriculum-title">
                  {curriculum.id === "state-syllabus" ? (
                    <Link to="/academics/state-syllabus" className="curriculum-title-link">
                      {curriculum.name} <span className="curriculum-title-arrow">→</span>
                    </Link>
                  ) : curriculum.id === "cbse" ? (
                    <Link to="/academics/cbse" className="curriculum-title-link">
                      {curriculum.name} <span className="curriculum-title-arrow">→</span>
                    </Link>
                  ) : curriculum.id === "icse" ? (
                    <Link to="/academics/icse" className="curriculum-title-link">
                      {curriculum.name} <span className="curriculum-title-arrow">→</span>
                    </Link>
                  ) : curriculum.id === "cambridge" ? (
                    <Link to="/academics/cambridge" className="curriculum-title-link">
                      {curriculum.name} <span className="curriculum-title-arrow">→</span>
                    </Link>
                  ) : curriculum.id === "pu-college" ? (
                    <Link to="/academics/pu-college" className="curriculum-title-link">
                      {curriculum.name} <span className="curriculum-title-arrow">→</span>
                    </Link>
                  ) : curriculum.id === "degree-college" ? (
                    <Link to="/academics/degree-college" className="curriculum-title-link">
                      {curriculum.name} <span className="curriculum-title-arrow">→</span>
                    </Link>
                  ) : (
                    curriculum.name
                  )}
                </h2>
                <p className="curriculum-board">{curriculum.board}</p>
                <p className="curriculum-description">
                  {curriculum.description}
                </p>

                <h3>Key Features:</h3>
                <ul className="features-list">
                  {curriculum.features.map((feature, idx) => (
                    <li key={idx}>{feature}</li>
                  ))}
                </ul>

                {/* <div className="curriculum-info">
                  <strong>Grades Offered:</strong> {curriculum.grades}
                </div> */}

                <div className="curriculum-cta">
                  <a
                    href={
                      curriculum.id === "pu-college" ||
                      curriculum.id === "degree-college"
                        ? PU_ENQUIRY_URL
                        : ENQUIRY_URL
                    }
                    className="btn btn-primary"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Apply for Admission
                  </a>
                  {curriculum.id === "state-syllabus" && (
                    <Link to="/academics/state-syllabus" className="btn btn-outline">
                      Learn More
                    </Link>
                  )}
                  {curriculum.id === "cbse" && (
                    <Link to="/academics/cbse" className="btn btn-outline">
                      Learn More
                    </Link>
                  )}
                  {curriculum.id === "icse" && (
                    <Link to="/academics/icse" className="btn btn-outline">
                      Learn More
                    </Link>
                  )}
                  {curriculum.id === "cambridge" && (
                    <Link to="/academics/cambridge" className="btn btn-outline">
                      Learn More
                    </Link>
                  )}
                  {curriculum.id === "pu-college" && (
                    <Link to="/academics/pu-college" className="btn btn-outline">
                      Learn More
                    </Link>
                  )}
                  {curriculum.id === "degree-college" && (
                    <Link to="/academics/degree-college" className="btn btn-outline">
                      Learn More
                    </Link>
                  )}
                </div>
              </motion.div>

              <motion.div
                className="curriculum-image"
                initial={{ opacity: 0, x: index % 2 === 0 ? 50 : -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <img src={curriculum.image} alt={curriculum.name} />
              </motion.div>
            </div>
          </div>
        </section>
      ))}

      {/* Curriculum Overview Section */}
      <section
        className="curriculum-overview-section section"
        id="curriculum-overview"
      >
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="section-title">Curriculum Overview</h2>
            <p className="section-subtitle">
              Comprehensive education focusing on academic excellence and
              holistic development
            </p>
          </motion.div>

          <div className="overview-content">
            <div className="overview-grid">
              <motion.div
                className="overview-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <h3>📖 Core Academics</h3>
                <ul>
                  <li>Mathematics & Sciences</li>
                  <li>Languages (English, Hindi, Kannada)</li>
                  <li>Social Sciences & Humanities</li>
                  <li>Computer Science & IT</li>
                </ul>
              </motion.div>

              <motion.div
                className="overview-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <h3>🎨 Co-curricular Activities</h3>
                <ul>
                  <li>Sports & Physical Education</li>
                  <li>Music, Dance & Drama</li>
                  <li>Art & Craft</li>
                  <li>Public Speaking & Debates</li>
                </ul>
              </motion.div>

              <motion.div
                className="overview-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <h3>💡 Life Skills</h3>
                <ul>
                  <li>Critical Thinking & Problem Solving</li>
                  <li>Communication & Collaboration</li>
                  <li>Leadership & Teamwork</li>
                  <li>Environmental Awareness</li>
                </ul>
              </motion.div>

              <motion.div
                className="overview-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <h3>🌟 Value Education</h3>
                <ul>
                  <li>Moral & Ethical Values</li>
                  <li>Cultural Heritage</li>
                  <li>Social Responsibility</li>
                  <li>Character Building</li>
                </ul>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Teaching Methodology Section */}
      <section
        className="methodology-section section"
        id="teaching-methodology"
      >
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="section-title">Teaching Methodology</h2>
            <p className="section-subtitle">
              Modern pedagogical approaches for effective learning
            </p>
          </motion.div>

          <div className="methodology-intro">
            <p>
              Our teaching methodology combines the best of traditional wisdom
              and modern innovation. We focus on student-centered learning,
              encouraging curiosity, creativity, and critical thinking. Our
              experienced faculty uses diverse teaching strategies to cater to
              different learning styles.
            </p>
          </div>

          <div className="methods-grid">
            {teachingMethods.map((method, index) => (
              <motion.div
                key={index}
                className="method-card card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="method-icon">{method.icon}</div>
                <h3>{method.title}</h3>
                <p>{method.description}</p>
              </motion.div>
            ))}
          </div>

          <div className="methodology-features">
            <h3>Our Teaching Approach Includes:</h3>
            <div className="grid grid-2">
              <ul className="approach-list">
                <li>Activity-based learning</li>
                <li>Collaborative group work</li>
                <li>Regular assessments & feedback</li>
                <li>Parent-teacher communication</li>
              </ul>
              <ul className="approach-list">
                <li>Use of educational technology</li>
                <li>Remedial & enrichment programs</li>
                <li>Inquiry-based learning</li>
                <li>Continuous professional development for teachers</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <motion.div
            className="cta-content"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>Find the Right Academic Path for Your Child</h2>
            {/* <p>Schedule a consultation with our academic counselors</p> */}
            <div className="cta-buttons">
              <AdmissionDropdown label="Apply for Admission" className="btn btn-secondary" />
              {/* <Link to="/contact" className="btn btn-secondary">Book a Campus Visit</Link> */}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Academics;
